<template>
    <div class="flex flex-col gap-3">
        <header class="flex items-center justify-between">
            <h4 class="font-medium text-slate-800 dark:text-slate-100">
                Redemption history
            </h4>
            <div class="flex justify-end items-center gap-2">
                <div class="relative" v-click-outside="closeDropdown">
                    <button
                        type="button"
                        class="flex items-center gap-1.5 px-1.5 py-1 rounded transition-colors hover:bg-slate-100 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                        @click="dropdownOpen = !dropdownOpen"
                    >
                        <Icon
                            icon="calendar"
                            class="w-5 h-5 text-slate-800 dark:text-slate-100"
                            svgClass="fill-none"
                            pathClass="fill-current"
                        />
                        <span
                            class="text-slate-800 dark:text-slate-100 font-inter text-sm font-medium leading-5"
                        >
                            {{ formatPeriodLabel(selectedPeriod) }}
                        </span>
                        <Icon
                            icon="chevron-down-small"
                            class="w-5 h-5 text-slate-800 dark:text-slate-100"
                        />
                    </button>
                    <!-- max-h + overflow caps the dropdown when the user has
                         8+ years of eligibility (Figma "Period And Navigation"
                         dev note: scroll if the list exceeds 6–8 records). -->
                    <div
                        v-if="dropdownOpen"
                        class="absolute top-full right-0 mt-1 z-30 flex flex-col p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-md min-w-[120px] max-h-60 overflow-y-auto"
                    >
                        <button
                            v-for="(period, i) in availablePeriods"
                            :key="`p-${period.startYear}-${period.endYear}-${i}`"
                            type="button"
                            class="flex p-2 items-center rounded text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 whitespace-nowrap"
                            :class="{
                                'bg-slate-100 dark:bg-slate-700 font-medium text-slate-800 dark:text-slate-100':
                                    isSamePeriod(period, selectedPeriod),
                            }"
                            @click="selectPeriod(period)"
                        >
                            {{ formatPeriodLabel(period) }}
                        </button>
                    </div>
                </div>

                <div class="w-px h-4 bg-slate-200 dark:bg-slate-600"></div>

                <div class="flex items-center gap-1">
                    <button
                        type="button"
                        :class="[
                            'flex w-6 h-6 justify-center items-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
                            canGoPrev
                                ? 'text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-600'
                                : 'cursor-not-allowed',
                        ]"
                        aria-label="Previous period"
                        :disabled="!canGoPrev"
                        @click="canGoPrev && shiftPeriod(-1)"
                    >
                        <Icon
                            icon="chevron-left-small"
                            :class="[
                                'w-5 h-5',
                                canGoPrev
                                    ? 'text-slate-800 dark:text-slate-100'
                                    : 'text-slate-300 dark:text-slate-600',
                            ]"
                        />
                    </button>
                    <button
                        type="button"
                        :class="[
                            'flex w-6 h-6 justify-center items-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400',
                            canGoNext
                                ? 'text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 active:bg-slate-200 dark:active:bg-slate-600'
                                : 'cursor-not-allowed',
                        ]"
                        aria-label="Next period"
                        :disabled="!canGoNext"
                        @click="canGoNext && shiftPeriod(1)"
                    >
                        <Icon
                            icon="chevron-right-small"
                            :class="[
                                'w-5 h-5',
                                canGoNext
                                    ? 'text-slate-800 dark:text-slate-100'
                                    : 'text-slate-300 dark:text-slate-600',
                            ]"
                        />
                    </button>
                </div>
            </div>
        </header>
        <div class="w-full h-[140px] relative">
            <Bar :data="chartData" :options="chartOptions" />
            <!-- Spec (Image #70): when there are no transactions during the
                 selected period, overlay a placeholder so the chart still
                 communicates "no data" instead of an axis full of empty bars. -->
            <div
                v-if="!hasData"
                class="absolute inset-0 flex items-center justify-center bg-white dark:bg-slate-800 pointer-events-none"
            >
                <ChartStatePlaceholder :loading="false" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import dayjs from 'dayjs';
import { Bar } from 'vue-chartjs';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from 'chart.js';
import {
    createExternalTooltip,
    formatTooltipMoney,
    isDarkMode,
    tooltipHeader,
    tooltipRow,
} from '@/components/card-benefits/chart-tooltip';
import Icon from '@/components/ui/icon/icon.vue';
import ChartStatePlaceholder from '@/components/card-benefits/insights/chart-state-placeholder.vue';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const props = defineProps({
    benefit: {
        type: Object,
        required: true,
    },
});

const MONTH_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

const dropdownOpen = ref(false);

const normalizePeriod = (periodType) => {
    if (!periodType) return null;
    return periodType.toUpperCase().replace(/-/g, '_');
};

const normalizedPeriodType = computed(() =>
    normalizePeriod(props.benefit?.benefit?.period_type)
);

// Cycle length (in years) per period_type. Anything ≤ 1 year shares the
// "single-year selector" behavior; multi-year cycles use range labels.
const cycleYears = computed(
    () =>
        ({
            QUADRENNIAL: 4,
            QUINQUENNIAL: 5,
        })[normalizedPeriodType.value] || 1
);

const isMultiYearCycle = computed(() => cycleYears.value > 1);

// Earliest year the user is eligible for this benefit. Spec (Image #68):
// "the earliest year in the list is the one when the user first became
// eligible". Use version_history.effective_start when present, fall back
// to oldest usage record, otherwise just the current year.
const eligibleStartYear = computed(() => {
    let earliest = dayjs().year();
    (props.benefit?.version_history || []).forEach((v) => {
        if (v?.effective_start) {
            const y = dayjs(v.effective_start).year();
            if (y && y < earliest) earliest = y;
        }
    });
    (props.benefit?.usage_records || []).forEach((r) => {
        if (r?.used_at) {
            const y = dayjs(r.used_at).year();
            if (y && y < earliest) earliest = y;
        }
    });
    return earliest;
});

// All selectable periods, newest first. For ≤ 1-year cycles each period is
// a single year; for multi-year cycles each entry is a year range anchored
// to the benefit's own period_start_date so cycles align with reality.
const availablePeriods = computed(() => {
    const currentYear = dayjs().year();
    const eligible = eligibleStartYear.value;
    const periods = [];

    if (isMultiYearCycle.value) {
        const cycle = cycleYears.value;
        const benefitStart = dayjs(props.benefit?.period_start_date);
        const benefitEnd = dayjs(props.benefit?.period_end_date);
        let cycleStart;
        let cycleEnd;
        if (benefitStart.isValid() && benefitEnd.isValid()) {
            cycleStart = benefitStart.year();
            cycleEnd = benefitEnd.year();
        } else {
            cycleEnd = currentYear;
            cycleStart = cycleEnd - cycle + 1;
        }
        while (cycleStart >= eligible) {
            periods.push({ startYear: cycleStart, endYear: cycleEnd });
            cycleStart -= cycle;
            cycleEnd -= cycle;
        }
        if (periods.length === 0) {
            periods.push({
                startYear: currentYear,
                endYear: currentYear + cycle - 1,
            });
        }
    } else {
        for (let y = currentYear; y >= eligible; y--) {
            periods.push({ startYear: y, endYear: y });
        }
        if (periods.length === 0) {
            periods.push({ startYear: currentYear, endYear: currentYear });
        }
    }
    return periods;
});

const selectedPeriod = ref(availablePeriods.value[0]);

// When the benefit changes (parent navigates between benefits via the chevrons
// in the sidebar header), reset to the newest period so the chart never shows
// stale years for a different benefit.
watch(
    () => props.benefit?.benefit?.card_benefit_id,
    () => {
        selectedPeriod.value = availablePeriods.value[0];
    }
);

const isSamePeriod = (a, b) =>
    a && b && a.startYear === b.startYear && a.endYear === b.endYear;

const formatPeriodLabel = (p) => {
    if (!p) return '';
    return p.startYear === p.endYear
        ? String(p.startYear)
        : `${p.startYear} – ${p.endYear}`;
};

const currentIdx = computed(() =>
    availablePeriods.value.findIndex((p) =>
        isSamePeriod(p, selectedPeriod.value)
    )
);
// Newest period is index 0; older periods grow toward the end of the array.
const canGoPrev = computed(
    () => currentIdx.value < availablePeriods.value.length - 1
);
const canGoNext = computed(() => currentIdx.value > 0);

const shiftPeriod = (direction) => {
    const newIdx = currentIdx.value - direction;
    if (newIdx >= 0 && newIdx < availablePeriods.value.length) {
        selectedPeriod.value = availablePeriods.value[newIdx];
    }
};

const selectPeriod = (period) => {
    selectedPeriod.value = period;
    dropdownOpen.value = false;
};

const closeDropdown = () => {
    dropdownOpen.value = false;
};

// Per-period dollar cap from the benefit. Count-based / always-active
// benefits resolve to 0 here, which suppresses the Unused fill.
const periodCap = computed(() =>
    parseFloat(props.benefit?.benefit?.amount || 0)
);

// Build the chart's period buckets. Each bucket carries start/end timestamps
// so we can decide past/current/future and match usage records against it
// regardless of period_type.
const buckets = computed(() => {
    const type = normalizedPeriodType.value;
    const period = selectedPeriod.value;
    if (!period) return [];

    const ranges = [];

    if (type === 'QUARTERLY') {
        const year = period.startYear;
        const qs = [
            { label: 'Jan – Mar', m0: 0, m1: 2 },
            { label: 'Apr – Jun', m0: 3, m1: 5 },
            { label: 'Jul – Sep', m0: 6, m1: 8 },
            { label: 'Oct – Dec', m0: 9, m1: 11 },
        ];
        qs.forEach((q) => {
            ranges.push({
                label: q.label,
                tooltipLabel: `${q.label}, ${year}`,
                start: dayjs(new Date(year, q.m0, 1)).startOf('day'),
                end: dayjs(new Date(year, q.m1, 1)).endOf('month'),
            });
        });
    } else if (
        type === 'SEMI_ANNUAL' ||
        type === 'SEMIANNUAL' ||
        type === 'BIANNUAL'
    ) {
        const year = period.startYear;
        const halves = [
            { label: 'Jan – Jun', m0: 0, m1: 5 },
            { label: 'Jul – Dec', m0: 6, m1: 11 },
        ];
        halves.forEach((h) => {
            ranges.push({
                label: h.label,
                tooltipLabel: `${h.label}, ${year}`,
                start: dayjs(new Date(year, h.m0, 1)).startOf('day'),
                end: dayjs(new Date(year, h.m1, 1)).endOf('month'),
            });
        });
    } else if (type === 'ANNUAL') {
        const year = period.startYear;
        ranges.push({
            label: String(year),
            tooltipLabel: String(year),
            start: dayjs(new Date(year, 0, 1)).startOf('day'),
            end: dayjs(new Date(year, 11, 31)).endOf('day'),
        });
    } else if (type === 'QUADRENNIAL' || type === 'QUINQUENNIAL') {
        const label = `${period.startYear} – ${period.endYear}`;
        ranges.push({
            label,
            tooltipLabel: label,
            start: dayjs(new Date(period.startYear, 0, 1)).startOf('day'),
            end: dayjs(new Date(period.endYear, 11, 31)).endOf('day'),
        });
    } else {
        // MONTHLY, STATEMENT, or unknown → 12 monthly buckets.
        const year = period.startYear;
        for (let m = 0; m < 12; m++) {
            const start = dayjs(new Date(year, m, 1)).startOf('day');
            ranges.push({
                label: MONTH_SHORT[m],
                tooltipLabel: `${MONTH_SHORT[m]}, ${year}`,
                start,
                end: start.endOf('month'),
            });
        }
    }

    const today = dayjs();
    const records = props.benefit?.usage_records || [];
    const cap = periodCap.value;

    const todayMs = today.valueOf();
    return ranges.map((b) => {
        // Compare via raw millisecond timestamps against the closed range
        // [b.start, b.end]. The previous implementation expanded each side
        // by one second, which — combined with dayjs's millisecond precision
        // — let a record at YYYY-MM-01T00:00:00 (the shape produced by the
        // auto-import path in CardBenefitUsageRecordService::createUsageRecord)
        // fall into BOTH the previous month's expanded end and the current
        // month's expanded start, double-counting every first-of-month usage
        // record (SUPPORT-697).
        const startMs = b.start.valueOf();
        const endMs = b.end.valueOf();
        const isCurrent = todayMs >= startMs && todayMs <= endMs;
        const isFuture = b.start.isAfter(today);
        const isPast = !isCurrent && !isFuture;

        let used = 0;
        records.forEach((r) => {
            const whenMs = dayjs(r.used_at).valueOf();
            if (whenMs >= startMs && whenMs <= endMs) {
                used += parseFloat(r.total_amount_used || 0);
            }
        });
        // Spec (Image #66, #69): Unused fill / tooltip row is only meaningful
        // for closed periods — current and future periods don't have an
        // "unused" yet. Count-based benefits (cap = 0) also skip it.
        const unused = isPast && cap > 0 ? Math.max(cap - used, 0) : 0;
        return { ...b, used, unused, isCurrent, isFuture, isPast };
    });
});

// Empty state: if no record falls inside the selected period, the chart
// would be all zeros. Spec (Image #70 right) calls for a placeholder
// instead of empty-axis bars.
const hasData = computed(() => buckets.value.some((b) => b.used > 0));

// Bar width auto-adapts: monthly (12 bars) stays slim; multi-year (1 bar)
// gets a much wider bar so it doesn't render as a thin sliver.
const maxBarThickness = computed(() => {
    const count = buckets.value.length || 1;
    if (count >= 8) return 18;
    if (count >= 3) return 36;
    if (count === 2) return 64;
    return 96;
});

const chartData = computed(() => ({
    labels: buckets.value.map((b) => b.label),
    datasets: [
        {
            label: 'Used',
            data: buckets.value.map((b) => b.used),
            backgroundColor: '#FD421D',
            borderRadius: {
                topLeft: 0,
                topRight: 0,
                bottomLeft: 2,
                bottomRight: 2,
            },
            borderSkipped: false,
            stack: 'usage',
            maxBarThickness: maxBarThickness.value,
        },
        {
            label: 'Unused',
            data: buckets.value.map((b) => b.unused),
            backgroundColor: '#CBD5E1',
            borderRadius: {
                topLeft: 2,
                topRight: 2,
                bottomLeft: 0,
                bottomRight: 0,
            },
            borderSkipped: false,
            stack: 'usage',
            maxBarThickness: maxBarThickness.value,
        },
    ],
}));

/**
 * Build a Y-axis max so `0 / max/2 / max` fall on clean integers and
 * Chart.js renders three tick marks (0, mid, top).
 */
const yMax = computed(() => {
    const peak = buckets.value.reduce(
        (max, b) => Math.max(max, b.used + b.unused),
        0
    );
    if (peak <= 0) return 10;
    const magnitude = Math.pow(10, Math.floor(Math.log10(peak)));
    for (const step of [1, 2, 4, 6, 10, 20, 40, 60, 100]) {
        const candidate = magnitude * step;
        if (candidate >= peak) return candidate;
    }
    return Math.ceil(peak / 2) * 2;
});

const chartOptions = computed(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: { display: false },
        datalabels: { display: false },
        tooltip: {
            enabled: false,
            external: createExternalTooltip({
                containerStyle: 'min-width: 168px; width: 168px;',
                buildBody: (model) => {
                    const idx = model.dataPoints?.[0]?.dataIndex;
                    if (idx == null) return '';
                    const bucket = buckets.value[idx];
                    if (!bucket) return '';

                    const headerHtml = tooltipHeader(bucket.tooltipLabel);
                    const usedHtml = tooltipRow({
                        dot: '#FD421D',
                        label: 'Used',
                        value: formatTooltipMoney(bucket.used),
                    });
                    // Spec (Image #69): hide the Unused row for current /
                    // future periods — it only makes sense after a period
                    // has closed.
                    if (bucket.isCurrent || bucket.isFuture) {
                        return `${headerHtml}${usedHtml}`;
                    }
                    const unusedHtml = tooltipRow({
                        dot: '#CBD5E1',
                        label: 'Unused',
                        value: formatTooltipMoney(bucket.unused),
                    });
                    return `${headerHtml}${usedHtml}${unusedHtml}`;
                },
            }),
        },
    },
    scales: {
        x: {
            stacked: true,
            grid: { display: false, drawBorder: false },
            ticks: {
                color: () => (isDarkMode() ? '#94A3B8' : '#64748B'),
                font: { size: 10 },
            },
        },
        y: {
            stacked: true,
            beginAtZero: true,
            min: 0,
            max: yMax.value,
            border: { display: false },
            grid: {
                color: () => (isDarkMode() ? '#334155' : '#F1F5F9'),
                drawBorder: false,
            },
            ticks: {
                color: () => (isDarkMode() ? '#94A3B8' : '#64748B'),
                font: { size: 10 },
                stepSize: yMax.value / 2,
                callback: (value) => value,
            },
        },
    },
}));
</script>
